import type { ReactNode } from "react";
import {
  Activity,
  Bill,
  Box,
  Chart21,
  ExportSquare,
  Grid3,
  Link as LinkIcon,
  Money,
  NotificationBing,
  People,
  Receipt,
  ReceiptDiscount,
  Setting5,
  Smileys,
  TruckFast,
  UserSearch,
  UserSquare,
  Wallet3,
  WalletMoney,
} from "iconsax-reactjs";
import type { Icon } from "iconsax-reactjs";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "../../../shared/ui";
import { DataTableColumn } from "../../../shared/ui/DataTable";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import { ActivityItem } from "../types";

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return `${datePart} - ${timePart}`;
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
};

const moduleIconClassName = "text-[#6F739B]";

const createModuleIcon = (Icon: Icon) => (
  <Icon size={18} className={moduleIconClassName} />
);

const moduleIcons: Record<string, ReactNode> = {
  dashboard: createModuleIcon(Grid3),
  auth: createModuleIcon(Grid3),
  "kyc & compliance": createModuleIcon(UserSquare),
  "kyc and compliance": createModuleIcon(UserSquare),
  "kyb & compliance": createModuleIcon(UserSquare),
  "kyb and compliance": createModuleIcon(UserSquare),
  customer: createModuleIcon(People),
  customers: createModuleIcon(People),
  "all customers": createModuleIcon(People),
  "ratings & feedback": createModuleIcon(Smileys),
  "ratings and feedback": createModuleIcon(Smileys),
  transactions: createModuleIcon(Chart21),
  transaction: createModuleIcon(Chart21),
  "payment links": createModuleIcon(LinkIcon),
  "bill payments": createModuleIcon(Bill),
  invoice: createModuleIcon(Receipt),
  invoices: createModuleIcon(Receipt),
  loans: createModuleIcon(Money),
  loan: createModuleIcon(Money),
  "payment requests": createModuleIcon(ReceiptDiscount),
  "payment request": createModuleIcon(ReceiptDiscount),
  "credit wallets": createModuleIcon(WalletMoney),
  "credit wallet": createModuleIcon(WalletMoney),
  procurement: createModuleIcon(Box),
  shipments: createModuleIcon(TruckFast),
  deployment: createModuleIcon(TruckFast),
  wallets: createModuleIcon(Wallet3),
  wallet: createModuleIcon(Wallet3),
  configurations: createModuleIcon(Setting5),
  configuration: createModuleIcon(Setting5),
  "team management": createModuleIcon(UserSearch),
  activity: createModuleIcon(Activity),
  notification: createModuleIcon(NotificationBing),
  notifications: createModuleIcon(NotificationBing),
  module: createModuleIcon(Bill),
};

export const getModuleIcon = (moduleName: string) => {
  return moduleIcons[getModuleKey(moduleName)] ?? createModuleIcon(Bill);
};

const getModuleKey = (moduleName: string) => {
  return moduleName.trim().toLowerCase();
};

const getTargetRoute = (moduleName: string, targetId?: string) => {
  if (!targetId) return null;

  switch (getModuleKey(moduleName)) {
    case "transactions":
    case "transaction":
      return `${PAGES.transactionsV2}/${targetId}`;
    case "invoice":
    case "invoices":
      return `/pay/invoice/${targetId}`;
    case "loans":
    case "loan":
      return `/finance/${targetId}`;
    case "kyc & compliance":
    case "kyc and compliance":
      return `/account/${targetId}`;
    default:
      return null;
  }
};

const renderLinkedValue = (label: string, to: string, withBrackets = false) => {
  const content = withBrackets ? `${label}` : label;

  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 text-[16px] text-primary-20 transition-opacity hover:opacity-80"
    >
      <span className="truncate">{content}</span>
      <ExportSquare size={18} />
    </Link>
  );
};

export const activityColumns: Array<DataTableColumn<ActivityItem>> = [
  {
    id: "member",
    header: "Member",
    cellClassName: "min-w-[180px]",
    cell: (item) => (
      <div className="flex items-center gap-3">
        <Avatar className="size-8 bg-[#EEF1FB]">
          {item.memberProfileImage ? (
            <AvatarImage
              src={item.memberProfileImage}
              alt={item.memberName}
              className="object-cover"
            />
          ) : null}
          <AvatarFallback className="bg-[#EEF1FB] text-[12px] font-[500] text-[#3B4060]">
            {getInitials(item.memberName)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate aku-body-small-medium">
          {item.memberName}
        </span>
      </div>
    ),
  },
  {
    id: "action",
    header: "Action",
    cellClassName: "min-w-[180px] aku-body-small-regular text-[#383B56]",
    cell: (item) => item.action,
  },
  {
    id: "module",
    header: "Module",
    cellClassName: "min-w-[140px] aku-body-small-regular",
    cell: (item) => (
      <div className="flex items-center gap-2">
        {getModuleIcon(item.module)}
        <span className="truncate text-[#383B56]">{item.module}</span>
      </div>
    ),
  },
  {
    id: "target",
    header: "Target",
    cellClassName: "min-w-[150px] aku-body-small-regular",
    cell: (item) => {
      const targetRoute = getTargetRoute(item.module, item.targetId);

      if (item.target === "-") {
        return <span className="text-[16px] text-primary-20">-</span>;
      }

      if (targetRoute) {
        return renderLinkedValue(item.target, targetRoute, true);
      }

      return <span className="text-[16px] text-primary-20">{item.target}</span>;
    },
  },
  {
    id: "outcome",
    header: "Outcome",
    cellClassName: "min-w-[100px] aku-body-small-medium",
    cell: (item) => (
      <span
        className={
          item.outcome === "Success" ? "text-[#11A811]" : "text-[#F73C3C]"
        }
      >
        {item.outcome}
      </span>
    ),
  },
  {
    id: "location",
    header: "Location",
    cellClassName: "min-w-[140px] aku-body-small-regular text-[#383B56]",
    cell: (item) => item.location,
  },
  {
    id: "timestamp",
    header: "Timestamp",
    cellClassName:
      "min-w-[160px] whitespace-nowrap aku-body-small-regular text-[#161827]",
    cell: (item) => formatTimestamp(item.timestamp),
  },
];
